import { ofType } from 'redux-observable'
import { of, timer } from 'rxjs'
import {
  catchError,
  concatMap,
  debounce,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  CREATE_VENDOR_ASYNC,
  FETCH_VENDOR_SETTINGS_ASYNC,
  FETCH_VENDOR_SUMMARY_ASYNC,
  FETCH_VENDOR_SYNC_STATUS_ASYNC,
  FETCH_VENDORS_ASYNC,
  IMPORT_VENDORS_ASYNC,
  isTerminalSyncStatus,
  REMOVE_VENDORS_ASYNC,
  RESTORE_VENDORS_ASYNC,
  SEARCH_DEBOUNCE_MS,
  SET_VENDOR_QUERY,
  START_VENDOR_SYNC_ASYNC,
  SYNC_POLL_ATTEMPTS,
  SYNC_POLL_INTERVAL_MS,
  UPDATE_VENDOR_SETTINGS_ASYNC,
  VENDOR_SYNC_POLL_EXHAUSTED,
} from './constants'
import { vendorErrorMessage } from './errors'
import { selectVendorListParams, selectVendorQuery } from './selectors'

const selectCompanyId = (state) => selectCompanyFilter(state)?.value ?? ''

const errorToast = (error, fallback) =>
  addToast({ message: vendorErrorMessage(error, fallback), type: 'error' })

// Only a search change waits; every other filter is a click.
const vendorQueryEpic = (action$) =>
  action$.pipe(
    ofType(SET_VENDOR_QUERY),
    debounce((action) => timer('search' in (action.payload || {}) ? SEARCH_DEBOUNCE_MS : 0)),
    map(() => FETCH_VENDORS_ASYNC.initiate())
  )

// Tenant-wide and filter-independent, so it must not refire on every keystroke.
const vendorSummaryQueryEpic = (action$) =>
  action$.pipe(
    ofType(SET_VENDOR_QUERY),
    filter((action) => 'window' in (action.payload || {})),
    map(() => FETCH_VENDOR_SUMMARY_ASYNC.initiate())
  )

// switchMap aborts the in-flight XHR when a newer query arrives.
const fetchVendorsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_VENDORS_ASYNC.INITIATE),
    switchMap(() => {
      const state = state$.value
      return api.vendors.list(selectVendorListParams(state), selectCompanyId(state)).pipe(
        map((response) => FETCH_VENDORS_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(FETCH_VENDORS_ASYNC.onError, error)))
      )
    })
  )

// Removing the last rows of the last page strands the user on a page that is gone.
const emptyPageEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_VENDORS_ASYNC.SUCCESS),
    filter(
      (action) =>
        !(action.payload?.items ?? []).length &&
        (action.payload?.total ?? 0) > 0 &&
        selectVendorQuery(state$.value).page > 1
    ),
    map(() => ({ type: SET_VENDOR_QUERY, payload: { page: 1 } }))
  )

const fetchVendorSummaryEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_VENDOR_SUMMARY_ASYNC.INITIATE),
    switchMap(() => {
      const state = state$.value
      return api.vendors.summary(selectVendorQuery(state).window, selectCompanyId(state)).pipe(
        map((response) => FETCH_VENDOR_SUMMARY_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(FETCH_VENDOR_SUMMARY_ASYNC.onError, error)))
      )
    })
  )

const fetchVendorSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_VENDOR_SETTINGS_ASYNC.INITIATE),
    switchMap(() =>
      api.vendors.autoDetection(selectCompanyId(state$.value)).pipe(
        map((response) => FETCH_VENDOR_SETTINGS_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(FETCH_VENDOR_SETTINGS_ASYNC.onError, error),
            errorToast(error, 'Failed to load the auto-detection setting.')
          )
        )
      )
    )
  )

// The list and the counts follow the switch, so both reload once the service has answered.
const updateVendorSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_VENDOR_SETTINGS_ASYNC.INITIATE),
    // exhaustMap: a second press while one save is open is dropped.
    exhaustMap((action) =>
      api.vendors.setAutoDetection(action.payload, selectCompanyId(state$.value)).pipe(
        mergeMap((response) =>
          of(
            UPDATE_VENDOR_SETTINGS_ASYNC.onSuccess(response),
            addToast({
              message: `Auto-detection turned ${response?.autoDetectionEnabled ? 'on' : 'off'}.`,
              type: 'success',
            }),
            FETCH_VENDORS_ASYNC.initiate(),
            FETCH_VENDOR_SUMMARY_ASYNC.initiate()
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(UPDATE_VENDOR_SETTINGS_ASYNC.onError, error),
            errorToast(error, 'Failed to change auto-detection.')
          )
        )
      )
    )
  )

const createVendorEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_VENDOR_ASYNC.INITIATE),
    mergeMap((action) => {
      const { body, fallbackName } = action.payload
      return api.vendors.create(body, selectCompanyId(state$.value)).pipe(
        mergeMap((response) => {
          const vendor = response?.response
          const name = vendor?.displayName || fallbackName
          // 200 is a removed or auto-detected vendor the add took over, now manual.
          const message = response?.status === 200 ? `${name} updated as manual.` : `${name} added.`
          return of(
            // A new vendor has no metrics, so an active filter can hide it.
            { type: SET_VENDOR_QUERY, payload: { search: '', authStatus: 'all' } },
            CREATE_VENDOR_ASYNC.onSuccess({ vendor }),
            addToast({
              message,
              type: 'success',
            })
          )
        }),
        catchError((error) => of(epicsErrorHandler(CREATE_VENDOR_ASYNC.onError, error)))
      )
    })
  )

const importVendorsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(IMPORT_VENDORS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.vendors.importFile(action.payload, selectCompanyId(state$.value)).pipe(
        // Partial success is the normal answer, so the list and tiles refresh either way.
        mergeMap((response) =>
          of(
            IMPORT_VENDORS_ASYNC.onSuccess(response),
            FETCH_VENDORS_ASYNC.initiate(),
            FETCH_VENDOR_SUMMARY_ASYNC.initiate()
          )
        ),
        // Shown inside the modal, where the file is, not as a page toast.
        catchError((error) => of(epicsErrorHandler(IMPORT_VENDORS_ASYNC.onError, error)))
      )
    )
  )

const bulkEpic =
  (types, call, successMessage, fallback) =>
  (action$, state$, { api, epicsErrorHandler }) =>
    action$.pipe(
      ofType(types.INITIATE),
      mergeMap((action) => {
        const vendors = action.payload || []
        const ids = vendors.map((vendor) => vendor.id)
        return api.vendors[call](ids, selectCompanyId(state$.value)).pipe(
          mergeMap((response) => {
            // Zero affected means every id was already gone: not a success, no undo.
            if (!response?.affected) {
              const message = `Nothing to ${call} - the list has been refreshed.`
              return of(
                types.onError({ detail: message }),
                addToast({ message, type: 'error' }),
                FETCH_VENDORS_ASYNC.initiate(),
                FETCH_VENDOR_SUMMARY_ASYNC.initiate()
              )
            }
            return of(
              types.onSuccess({ vendors }),
              ...(successMessage ? [addToast({ message: successMessage, type: 'success' })] : []),
              FETCH_VENDORS_ASYNC.initiate(),
              FETCH_VENDOR_SUMMARY_ASYNC.initiate()
            )
          }),
          catchError((error) =>
            of(epicsErrorHandler(types.onError, error), errorToast(error, fallback))
          )
        )
      })
    )

const removeVendorsEpic = bulkEpic(
  REMOVE_VENDORS_ASYNC,
  'remove',
  null,
  'Failed to remove the selected vendors.'
)

const restoreVendorsEpic = bulkEpic(
  RESTORE_VENDORS_ASYNC,
  'restore',
  'Vendor removal undone.',
  'Failed to restore the selected vendors.'
)

// A press is accepted with 202; the outcome arrives through the status poll.
const startVendorSyncEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(START_VENDOR_SYNC_ASYNC.INITIATE),
    // exhaustMap: a press that slips past the disabled button is dropped.
    exhaustMap(() => {
      // Read once: the poll belongs to the tenant that was pressed.
      const companyId = selectCompanyId(state$.value)
      return api.vendors.startSync(companyId).pipe(
        mergeMap((response) =>
          of(
            START_VENDOR_SYNC_ASYNC.onSuccess(response),
            FETCH_VENDOR_SYNC_STATUS_ASYNC.initiate(companyId)
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(START_VENDOR_SYNC_ASYNC.onError, error),
            errorToast(error, 'Failed to start the vendor data sync.')
          )
        )
      )
    })
  )

// foundInSnapshot false is a 200, not a failure.
const syncToast = (response) => {
  if (response?.status === 'FAILED') {
    return { message: 'The vendor data sync did not complete. Try again.', type: 'error' }
  }
  if (response?.foundInSnapshot === false) {
    return { message: 'No vendor activity found for your company yet.', type: 'info' }
  }
  return { message: 'Vendor data synced.', type: 'success' }
}

// The list and the tiles only change once the run has merged.
const syncPollActions = (response, exhausted) => {
  if (isTerminalSyncStatus(response?.status)) {
    return of(
      FETCH_VENDOR_SYNC_STATUS_ASYNC.onSuccess(response),
      addToast(syncToast(response)),
      FETCH_VENDORS_ASYNC.initiate(),
      FETCH_VENDOR_SUMMARY_ASYNC.initiate()
    )
  }
  if (exhausted) {
    return of(
      { type: VENDOR_SYNC_POLL_EXHAUSTED },
      addToast({
        message: 'The sync is still running. Reload the page to see how it ended.',
        type: 'info',
      })
    )
  }
  return of(FETCH_VENDOR_SYNC_STATUS_ASYNC.onSuccess(response))
}

// Asks every 5 seconds until the run is terminal, or the ceiling is reached.
const pollVendorSyncEpic = (action$, _state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_VENDOR_SYNC_STATUS_ASYNC.INITIATE),
    // switchMap: a fresh press replaces the loop rather than running two.
    switchMap((action) =>
      timer(SYNC_POLL_INTERVAL_MS, SYNC_POLL_INTERVAL_MS).pipe(
        // A tenant switch ends the loop, or the next ask would carry the new one.
        takeUntil(action$.pipe(ofType(COMPANY_FILTER_CHANGE))),
        take(SYNC_POLL_ATTEMPTS),
        // concatMap, so a slow answer delays the next ask instead of stacking.
        concatMap((attempt) =>
          api.vendors
            .syncStatus(action.payload ?? '')
            .pipe(map((response) => [response, attempt === SYNC_POLL_ATTEMPTS - 1]))
        ),
        // Inclusive: the terminal answer is the one that has to be acted on.
        takeWhile(
          ([response, exhausted]) => !isTerminalSyncStatus(response?.status) && !exhausted,
          true
        ),
        mergeMap(([response, exhausted]) => syncPollActions(response, exhausted)),
        catchError((error) =>
          of(
            epicsErrorHandler(FETCH_VENDOR_SYNC_STATUS_ASYNC.onError, error),
            errorToast(error, 'Failed to read the vendor data sync status.')
          )
        )
      )
    )
  )

// The create epic's SET_VENDOR_QUERY already refetched the list; this is the counts.
const refreshSummaryAfterCreateEpic = (action$) =>
  action$.pipe(
    ofType(CREATE_VENDOR_ASYNC.SUCCESS),
    map(() => FETCH_VENDOR_SUMMARY_ASYNC.initiate())
  )

export default combineEpicsIsolated(
  vendorQueryEpic,
  vendorSummaryQueryEpic,
  fetchVendorsEpic,
  emptyPageEpic,
  fetchVendorSummaryEpic,
  fetchVendorSettingsEpic,
  updateVendorSettingsEpic,
  createVendorEpic,
  refreshSummaryAfterCreateEpic,
  importVendorsEpic,
  removeVendorsEpic,
  restoreVendorsEpic,
  startVendorSyncEpic,
  pollVendorSyncEpic
)
